import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import type { TopNRequest } from '@statistik/shared-types';
import { fmtCurrency, fmtCurrencyCompact, fmtNumber, fmtPercent } from '../lib/format.ts';
import { COLORS, axisStyle, tooltipStyle } from '../lib/echarts-theme.ts';
import { useIsDark } from '../lib/useIsDark.ts';
import { ChartSkeleton } from '../components/Skeleton.tsx';
import { Icon } from '../components/Icon.tsx';

interface Props {
  dimension: TopNRequest['dimension'];
  measure: TopNRequest['measure'];
  limit: number;
}

const DIM_TO_FILTER_KEY: Record<string, string> = {
  store_name: 'store_name',
  store_number: 'store_number',
  warengruppe_name: 'warengruppe_name',
  artikel_bezeichnung: 'artikel_bezeichnung',
  artikel_nr: 'artikel_nr',
  ean: 'ean',
  kassierer_name: 'kassierer_name',
  plz: 'plz',
};

export function TopNChart({ dimension, measure, limit }: Props) {
  const filters = useFilterStore((s) => s.filters);
  const addFilter = useFilterStore((s) => s.addFilter);
  const dark = useIsDark();
  const { data, isLoading } = useQuery({
    queryKey: ['topn', dimension, measure, limit, filters],
    queryFn: () => api.topn({ filters, dimension, measure, limit }),
  });

  // Höhe dynamisch nach Anzahl der Einträge — kompaktere Reihen
  const rowHeight = 26;
  const chartHeight = Math.max(220, (data?.length ?? limit) * rowHeight + 30);

  if (isLoading) return <ChartSkeleton height={chartHeight} />;

  if (!data || data.length === 0) {
    return (
      <div className="py-10 px-4 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800/60 text-slate-400 dark:text-slate-500 flex items-center justify-center mx-auto mb-3">
          <Icon name="search" size={20} />
        </div>
        <div className="text-sm font-medium text-slate-600 dark:text-slate-300">
          Keine Daten
        </div>
        <div className="text-2xs text-slate-400 dark:text-slate-500 mt-1">
          Im aktuellen Filter liegen keine Werte vor.
        </div>
      </div>
    );
  }

  // ECharts erwartet umgekehrte Reihenfolge bei horizontal-Bar (größtes oben)
  const reversed = [...data].reverse();
  // Sub-Label-Rendering je nach Dimension:
  //   - Warengruppe → WG-Nummer als Präfix: "3 — Fleisch"
  //   - Artikel → Mono-Artikelnummer als Suffix: "Fleisch · #12345"
  const isWg = dimension === 'warengruppe_name';
  const labels = reversed.map((d) =>
    d.sub_label
      ? isWg
        ? `${d.sub_label} — ${d.label}`
        : `${d.label}  ·  #${d.sub_label}`
      : d.label,
  );
  const values = reversed.map((d) => d.wert);

  const option = {
    grid: { top: 6, right: 60, bottom: 22, left: 4, containLabel: true },
    tooltip: {
      ...tooltipStyle(),
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: Array<{ name: string; value: number; dataIndex: number }>) => {
        const p = params[0];
        const item = reversed[p.dataIndex];
        const v =
          measure === 'umsatz'
            ? fmtCurrency(p.value)
            : measure === 'menge'
              ? `${fmtNumber(p.value, 1)} Stück`
              : `${fmtNumber(p.value)} Bons`;
        const title = item?.label ?? p.name;
        const sub = item?.sub_label
          ? isWg
            ? `<div style="color:#94a3b8;font-size:11px">WG-Nummer ${item.sub_label}</div>`
            : `<div style="color:#94a3b8;font-size:11px;font-family:Consolas,monospace">Artikel-Nr #${item.sub_label}</div>`
          : '';
        return `<div style="font-size:12px;max-width:280px">
          <div style="font-weight:600;margin-bottom:2px">${title}</div>
          ${sub}
          <div style="font-variant-numeric:tabular-nums;margin-top:4px">${v}</div>
          <div style="color:#94a3b8;font-size:11px;margin-top:2px">${fmtPercent(item?.anteil ?? 0)} Anteil</div>
        </div>`;
      },
    },
    xAxis: {
      type: 'value',
      ...axisStyle(),
      axisLabel: {
        ...axisStyle().axisLabel,
        formatter: (v: number) =>
          measure === 'umsatz' ? fmtCurrencyCompact(v) : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v),
      },
    },
    yAxis: {
      type: 'category',
      data: labels,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: dark ? COLORS.darkText : COLORS.text,
        fontSize: 11,
        fontFamily: 'Inter, sans-serif',
        width: 120,
        overflow: 'truncate',
      },
    },
    series: [
      {
        type: 'bar',
        data: values,
        barCategoryGap: '25%',
        barMinHeight: 4,
        // Flacher Präzisions-Balken statt Verlaufs-Deko; Hover hebt klar an.
        itemStyle: {
          color: '#0e7bd1',
          borderRadius: [0, 2, 2, 0],
        },
        emphasis: {
          itemStyle: { color: dark ? '#4cb1ff' : '#0a3a64' },
        },
        label: {
          show: true,
          position: 'right',
          distance: 4,
          formatter: (p: { value: number }) =>
            measure === 'umsatz' ? fmtCurrencyCompact(p.value) : fmtNumber(p.value),
          fontSize: 10,
          color: dark ? COLORS.darkText : COLORS.text,
          fontWeight: 600,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: chartHeight }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
      onEvents={{
        click: (params: { name: string; dataIndex: number }) => {
          const filterKey = DIM_TO_FILTER_KEY[dimension] ?? dimension;
          // Beim Klick den Original-Label-Wert verwenden (ohne sub_label-Suffix)
          const raw = reversed[params.dataIndex]?.label ?? params.name;
          addFilter(filterKey, raw);
        },
      }}
    />
  );
}
